import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useTable} from 'react-table';
import * as actions from '../../actions/ContactsActions';
import * as columnFormatters from '../../../_common/column-formatters';
import * as customColumnFormatters from '../column-formatters';
import {useContactsContext} from '../ContactsContext';
import useInfiniteScrollControl from '../../../_common/components/_custom-hooks/useInfiniteScrollControl';
import CommonGrid from '../../../_common/components/CommonGrid';
import CommonGridSpinner from '../../../_common/components/CommonGridSpinner';
import SortingColumnHeader from "../../../_common/components/SortingColumnHeader/SortingColumnHeader";

export default function ContactsTable() {
  const intl = useIntl();
  const [tableRows, setTableRows] = useState([]);
  const prefix = 'contacts-column';
  const columns = useMemo(() => [
    {
      accessor: 'contactID',
      Header:
        <SortingColumnHeader
          text={intl.formatMessage({id: `${prefix}-contactID`, defaultMessage: 'Contact ID'})}
          fieldName={'contactID'}
          useContext={useContactsContext}
          showDefaultIcon={true}
        />
    },
    {
      accessor: 'fullName',
      Header:
        <SortingColumnHeader
          text={intl.formatMessage({id: `${prefix}-fullName`, defaultMessage: 'Full name'})}
          fieldName={'fullName'}
          useContext={useContactsContext}
        />
    },
    {
      accessor: 'phone',
      Header:
        <SortingColumnHeader
          text={intl.formatMessage({id: `${prefix}-phone`, defaultMessage: 'Phone'})}
          fieldName={'phone'}
          useContext={useContactsContext}
        />
    },
    {
      accessor: 'email',
      Header:
        <SortingColumnHeader
          text={intl.formatMessage({id: `${prefix}-email`, defaultMessage: 'Email'})}
          fieldName={'email'}
          useContext={useContactsContext}
        />
    },
    {
      accessor: 'job',
      Header:
        <SortingColumnHeader
          text={intl.formatMessage({id: `${prefix}-job`, defaultMessage: 'Job'})}
          fieldName={'job'}
          useContext={useContactsContext}
        />
    },
    {
      accessor: 'organization',
      Header:
        <SortingColumnHeader
          text={intl.formatMessage({id: `${prefix}-organization`, defaultMessage: 'Organization'})}
          fieldName={'organization'}
          useContext={useContactsContext}
        />
    },
    {
      accessor: 'address',
      Header:
        <SortingColumnHeader
          text={intl.formatMessage({id: `${prefix}-address`, defaultMessage: 'Address'})}
          fieldName={'address'}
          useContext={useContactsContext}
        />
    },
    {
      accessor: 'statusName',
      Header:
        <SortingColumnHeader
          text={intl.formatMessage({id: `${prefix}-statusName`, defaultMessage: 'Status'})}
          fieldName={'statusName'}
          useContext={useContactsContext}
        />
    },
    {
      accessor: 'actions',
      Header: '', width: 90
    },
  ], []);
  const data = useMemo(() => [
    ...tableRows.map((item) => {
      return {
        contactID: columnFormatters.BoldColumnFormatter(item.contactID, 'xl'),
        fullName: columnFormatters.BoldColumnFormatter([item.firstName, item.lastName].filter(Boolean).join(' ')),
        phone: columnFormatters.BoldColumnFormatter(item.phone),
        email: columnFormatters.BoldLTRColumnFormatter(item.email),
        job: columnFormatters.BoldColumnFormatter(item.job),
        organization: columnFormatters.BoldColumnFormatter(item.organization),
        address: columnFormatters.BoldColumnFormatter(item.address),
        statusName: customColumnFormatters.StatusColumnFormatter(item),
        actions: customColumnFormatters.ContactsActionsColumnFormatter(item),
      };
    })
  ], [tableRows]);

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'contactsReducer',
    UIContext: useContactsContext(),
    getNewEntities: actions.getContacts,
    getNextEntities: actions.getNextContacts,
    onEntitiesChange: entities => {
      entities
        ? setTableRows(entities)
        : setTableRows([]);
    },
    startRequestedPageNumber: 0,
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: (<CommonGridSpinner/>),
    style: {
      overflow: 'inherit'
    },
  });

  return (
    <CommonGrid
      infiniteScrollProps={infiniteScrollProps}
      {...useTable({columns, data})}
    />
  );
}
